/**
 * Install Agent Modal
 * Multi-step wizard for installing new agents
 */

import { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { Button, EmojiPicker } from '@/components/Common';
import type {
  AgentInstallRequest,
  AgentFramework,
  AgentCapability,
  AgentConfig,
  AzureAIAgentConfig,
  CopilotConfig,
  MCPServerConfig,
} from '@/types/agent.types';

interface InstallAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInstall: (request: AgentInstallRequest) => Promise<void>;
}

type Step = 'framework' | 'details' | 'config' | 'review';

const DEFAULT_AZURE_API_VERSION = '2025-02-15-preview';

function buildDefaultConfig(framework: AgentFramework): AgentConfig {
  switch (framework) {
    case 'azure-ai-agent':
      return {
        framework: 'azure-ai-agent',
        config: {
          endpoint: '',
          apiKey: '',
          deploymentId: '',
          apiVersion: DEFAULT_AZURE_API_VERSION,
        },
      };
    case 'copilot':
      return {
        framework: 'copilot',
        config: { endpoint: '', apiKey: '', model: '' },
      };
    case 'mcp':
      return {
        framework: 'mcp',
        config: { command: '' },
      };
  }
}

const FRAMEWORKS: Array<{
  value: AgentFramework;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    value: 'mcp',
    label: 'MCP Server',
    description: 'Model Context Protocol server with custom tools',
    icon: '🔌',
  },
  {
    value: 'copilot',
    label: 'GitHub Copilot',
    description: 'AI-powered code assistant and chat',
    icon: '🤖',
  },
  {
    value: 'azure-ai-agent',
    label: 'Azure AI Agent',
    description: 'Azure AI Agent Framework with built-in tools',
    icon: '☁️',
  },
];

const CAPABILITIES: Array<{ value: AgentCapability; label: string }> = [
  { value: 'chat', label: 'Chat' },
  { value: 'search', label: 'Search' },
  { value: 'code-generation', label: 'Code Generation' },
  { value: 'data-analysis', label: 'Data Analysis' },
  { value: 'document-processing', label: 'Document Processing' },
  { value: 'custom', label: 'Custom' },
];

export function InstallAgentModal({
  isOpen,
  onClose,
  onInstall,
}: InstallAgentModalProps) {
  const [step, setStep] = useState<Step>('framework');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mcpArgsRaw, setMcpArgsRaw] = useState('');
  const [formData, setFormData] = useState<Partial<AgentInstallRequest>>({
    capabilities: ['chat'],
  });

  if (!isOpen) return null;

  const updateFormData = (updates: Partial<AgentInstallRequest>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSelectFramework = (framework: AgentFramework) => {
    if (formData.framework === framework) return;
    setMcpArgsRaw('');
    setFormData((prev) => ({
      ...prev,
      framework,
      config: buildDefaultConfig(framework),
    }));
  };

  const updateAzureConfig = (patch: Partial<AzureAIAgentConfig>) => {
    setFormData((prev) => {
      const base =
        prev.config?.framework === 'azure-ai-agent'
          ? prev.config
          : (buildDefaultConfig('azure-ai-agent') as Extract<
              AgentConfig,
              { framework: 'azure-ai-agent' }
            >);
      return {
        ...prev,
        config: { framework: 'azure-ai-agent', config: { ...base.config, ...patch } },
      };
    });
  };

  const updateCopilotConfig = (patch: Partial<CopilotConfig>) => {
    setFormData((prev) => {
      const base =
        prev.config?.framework === 'copilot'
          ? prev.config
          : (buildDefaultConfig('copilot') as Extract<
              AgentConfig,
              { framework: 'copilot' }
            >);
      return {
        ...prev,
        config: { framework: 'copilot', config: { ...base.config, ...patch } },
      };
    });
  };

  const updateMcpConfig = (patch: Partial<MCPServerConfig>) => {
    setFormData((prev) => {
      const base =
        prev.config?.framework === 'mcp'
          ? prev.config
          : (buildDefaultConfig('mcp') as Extract<
              AgentConfig,
              { framework: 'mcp' }
            >);
      return {
        ...prev,
        config: { framework: 'mcp', config: { ...base.config, ...patch } },
      };
    });
  };

  const azureConfig =
    formData.config?.framework === 'azure-ai-agent' ? formData.config.config : null;
  const copilotConfig =
    formData.config?.framework === 'copilot' ? formData.config.config : null;
  const mcpConfig =
    formData.config?.framework === 'mcp' ? formData.config.config : null;

  const handleNext = () => {
    const steps: Step[] = ['framework', 'details', 'config', 'review'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const steps: Step[] = ['framework', 'details', 'config', 'review'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1]);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onInstall(formData as AgentInstallRequest);
      handleClose();
    } catch (error) {
      console.error('Failed to install agent:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('framework');
    setFormData({ capabilities: ['chat'] });
    setMcpArgsRaw('');
    onClose();
  };

  const canProceed = () => {
    switch (step) {
      case 'framework':
        return !!formData.framework;
      case 'details':
        return !!formData.name && !!formData.description;
      case 'config': {
        if (azureConfig) {
          return (
            azureConfig.endpoint.trim() !== '' &&
            azureConfig.apiKey.trim() !== '' &&
            azureConfig.deploymentId.trim() !== ''
          );
        }
        if (copilotConfig) {
          return (
            copilotConfig.endpoint.trim() !== '' &&
            copilotConfig.apiKey.trim() !== ''
          );
        }
        if (mcpConfig) {
          return mcpConfig.command.trim() !== '';
        }
        return true;
      }
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const maskKey = (key: string) =>
    key.length <= 4 ? '••••' : `••••${key.slice(-4)}`;

  const stepLabels: Record<Step, string> = {
    framework: 'Select Framework',
    details: 'Agent Details',
    config: 'Configuration',
    review: 'Review and Install',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        className="relative w-full max-w-2xl rounded-lg bg-bg-primary shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-default px-6 py-4">
          <h2
            id="modal-title"
            className="text-xl font-semibold text-text-primary"
          >
            Install New Agent
          </h2>
          <button
            onClick={handleClose}
            className={
              'rounded p-1 hover:bg-bg-secondary ' +
              'focus-visible:outline-none focus-visible:ring-2 ' +
              'focus-visible:ring-stellar-primary'
            }
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="border-b border-border-default px-6 py-4">
          <div
            className="flex items-center justify-between"
            role="progressbar"
            aria-valuenow={['framework', 'details', 'config', 'review'].indexOf(step) + 1}
            aria-valuemin={1}
            aria-valuemax={4}
            aria-label={`Step ${['framework', 'details', 'config', 'review'].indexOf(step) + 1} of 4: ${stepLabels[step]}`}
          >
            {['Framework', 'Details', 'Config', 'Review'].map((label, idx) => {
              const steps: Step[] = ['framework', 'details', 'config', 'review'];
              const currentIdx = steps.indexOf(step);
              const isActive = idx === currentIdx;
              const isComplete = idx < currentIdx;

              return (
                <div key={label} className="flex items-center">
                  <div
                    className={
                      `flex h-8 w-8 items-center justify-center ` +
                      `rounded-full text-sm font-medium ${
                        isComplete
                          ? 'bg-success text-white'
                          : isActive
                            ? 'bg-stellar-primary text-white'
                            : 'bg-bg-tertiary text-text-secondary'
                      }`
                    }
                    aria-label={
                      isComplete
                        ? `${label} completed`
                        : isActive
                          ? `${label} current step`
                          : `${label} pending`
                    }
                  >
                    {isComplete ? <Check className="h-4 w-4" /> : idx + 1}
                  </div>
                  <span
                    className={
                      `ml-2 text-sm ${
                        isActive
                          ? 'font-medium text-text-primary'
                          : 'text-text-secondary'
                      }`
                    }
                  >
                    {label}
                  </span>
                  {idx < 3 && (
                    <ChevronRight className="mx-4 h-4 w-4 text-text-tertiary" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div
          id="modal-description"
          className="max-h-96 overflow-y-auto px-6 py-6"
        >
          {step === 'framework' && (
            <div className="space-y-4">
              <p className="text-sm text-text-secondary">
                Select the agent framework you want to use
              </p>
              <div className="space-y-3">
                {FRAMEWORKS.map((framework) => (
                  <button
                    key={framework.value}
                    onClick={() => handleSelectFramework(framework.value)}
                    className={
                      `w-full rounded-lg border-2 p-4 text-left ` +
                      `transition-all ${
                        formData.framework === framework.value
                          ? 'border-stellar-primary bg-bg-tertiary'
                          : 'border-border-default hover:border-border-strong'
                      }`
                    }
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{framework.icon}</span>
                      <div>
                        <h3 className="font-medium text-text-primary">
                          {framework.label}
                        </h3>
                        <p className="mt-1 text-sm text-text-secondary">
                          {framework.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'details' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary">
                  Agent Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => updateFormData({ name: e.target.value })}
                  placeholder="My Custom Agent"
                  className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary">
                  Description *
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) =>
                    updateFormData({ description: e.target.value })
                  }
                  placeholder="Describe what this agent does..."
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary">
                  Icon (emoji)
                </label>
                <EmojiPicker
                  value={formData.icon || ''}
                  onChange={(emoji) => updateFormData({ icon: emoji })}
                  placeholder="Select agent icon"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary">
                  Capabilities
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {CAPABILITIES.map((capability) => (
                    <button
                      key={capability.value}
                      onClick={() => {
                        const current = formData.capabilities || [];
                        const updated = current.includes(capability.value)
                          ? current.filter((c) => c !== capability.value)
                          : [...current, capability.value];
                        updateFormData({ capabilities: updated });
                      }}
                      className={
                        `rounded-full px-3 py-1 text-sm font-medium ` +
                        `transition-all ${
                          formData.capabilities?.includes(capability.value)
                            ? 'bg-stellar-primary text-white'
                            : 'bg-bg-secondary text-text-primary hover:bg-bg-tertiary'
                        }`
                      }
                    >
                      {capability.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'config' && (
            <div className="space-y-4">
              <p className="text-sm text-text-secondary">
                Configure your {formData.framework} agent. You can leave fields
                blank and edit them later.
              </p>

              {formData.framework === 'azure-ai-agent' && azureConfig && (
                <>
                  <div>
                    <label
                      htmlFor="agent-azure-endpoint"
                      className="block text-sm font-medium text-text-primary"
                    >
                      Endpoint *
                    </label>
                    <input
                      id="agent-azure-endpoint"
                      type="text"
                      value={azureConfig.endpoint}
                      onChange={(e) =>
                        updateAzureConfig({ endpoint: e.target.value })
                      }
                      placeholder="https://your-resource.openai.azure.com"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="agent-azure-key"
                      className="block text-sm font-medium text-text-primary"
                    >
                      API Key *
                    </label>
                    <input
                      id="agent-azure-key"
                      type="password"
                      autoComplete="off"
                      value={azureConfig.apiKey}
                      onChange={(e) =>
                        updateAzureConfig({ apiKey: e.target.value })
                      }
                      placeholder="Azure OpenAI API key"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="agent-azure-deployment"
                      className="block text-sm font-medium text-text-primary"
                    >
                      Deployment ID *
                    </label>
                    <input
                      id="agent-azure-deployment"
                      type="text"
                      value={azureConfig.deploymentId}
                      onChange={(e) =>
                        updateAzureConfig({ deploymentId: e.target.value })
                      }
                      placeholder="gpt-4"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="agent-azure-version"
                      className="block text-sm font-medium text-text-primary"
                    >
                      API Version
                    </label>
                    <input
                      id="agent-azure-version"
                      type="text"
                      value={azureConfig.apiVersion ?? ''}
                      onChange={(e) =>
                        updateAzureConfig({ apiVersion: e.target.value })
                      }
                      placeholder={DEFAULT_AZURE_API_VERSION}
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                </>
              )}

              {formData.framework === 'copilot' && copilotConfig && (
                <>
                  <div>
                    <label
                      htmlFor="agent-copilot-endpoint"
                      className="block text-sm font-medium text-text-primary"
                    >
                      API Endpoint *
                    </label>
                    <input
                      id="agent-copilot-endpoint"
                      type="text"
                      value={copilotConfig.endpoint}
                      onChange={(e) =>
                        updateCopilotConfig({ endpoint: e.target.value })
                      }
                      placeholder="https://api.githubcopilot.com"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="agent-copilot-key"
                      className="block text-sm font-medium text-text-primary"
                    >
                      API Key *
                    </label>
                    <input
                      id="agent-copilot-key"
                      type="password"
                      autoComplete="off"
                      value={copilotConfig.apiKey}
                      onChange={(e) =>
                        updateCopilotConfig({ apiKey: e.target.value })
                      }
                      placeholder="GitHub Copilot API key"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="agent-copilot-model"
                      className="block text-sm font-medium text-text-primary"
                    >
                      Model
                    </label>
                    <input
                      id="agent-copilot-model"
                      type="text"
                      value={copilotConfig.model ?? ''}
                      onChange={(e) =>
                        updateCopilotConfig({ model: e.target.value })
                      }
                      placeholder="gpt-4"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                </>
              )}

              {formData.framework === 'mcp' && mcpConfig && (
                <>
                  <div>
                    <label
                      htmlFor="agent-mcp-command"
                      className="block text-sm font-medium text-text-primary"
                    >
                      Command *
                    </label>
                    <input
                      id="agent-mcp-command"
                      type="text"
                      value={mcpConfig.command}
                      onChange={(e) =>
                        updateMcpConfig({ command: e.target.value })
                      }
                      placeholder="node"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="agent-mcp-args"
                      className="block text-sm font-medium text-text-primary"
                    >
                      Arguments (comma-separated)
                    </label>
                    <input
                      id="agent-mcp-args"
                      type="text"
                      value={mcpArgsRaw}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setMcpArgsRaw(raw);
                        const parsed = raw
                          .split(',')
                          .map((s) => s.trim())
                          .filter((s) => s.length > 0);
                        updateMcpConfig({
                          args: parsed.length > 0 ? parsed : undefined,
                        });
                      }}
                      placeholder="server.js, --port, 3000"
                      className="mt-1 w-full rounded-lg border border-border-medium px-3 py-2 bg-bg-primary text-text-primary placeholder-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-1 focus:ring-stellar-primary"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'review' && (
            <div className="space-y-4">
              <p className="text-sm text-text-secondary">
                Review your agent configuration
              </p>

              <div className="rounded-lg border border-border-default bg-bg-secondary p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{formData.icon || '🤖'}</span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-text-primary">
                      {formData.name}
                    </h3>
                    <p className="mt-1 text-sm text-text-secondary">
                      {formData.description}
                    </p>
                  </div>
                </div>

                <div className="border-t border-border-default pt-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Framework:</span>
                    <span className="font-medium">
                      {FRAMEWORKS.find((f) => f.value === formData.framework)
                        ?.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Capabilities:</span>
                    <span className="font-medium">
                      {formData.capabilities?.length || 0} selected
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  {formData.capabilities?.map((cap) => (
                    <span
                      key={cap}
                      className="rounded-full bg-bg-tertiary px-2 py-1 text-xs font-medium text-text-primary"
                    >
                      {cap}
                    </span>
                  ))}
                </div>

                {(azureConfig || copilotConfig || mcpConfig) && (
                  <div className="border-t border-border-default pt-3 space-y-1 text-sm">
                    <div className="font-medium text-text-primary">
                      Configuration
                    </div>
                    {azureConfig && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Endpoint:</span>
                          <span className="font-mono text-xs text-text-primary truncate max-w-[60%]">
                            {azureConfig.endpoint || '(blank)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">API Key:</span>
                          <span className="font-mono text-xs text-text-primary">
                            {azureConfig.apiKey
                              ? maskKey(azureConfig.apiKey)
                              : '(blank)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">
                            Deployment:
                          </span>
                          <span className="font-mono text-xs text-text-primary">
                            {azureConfig.deploymentId || '(blank)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">
                            API Version:
                          </span>
                          <span className="font-mono text-xs text-text-primary">
                            {azureConfig.apiVersion || '(default)'}
                          </span>
                        </div>
                      </>
                    )}
                    {copilotConfig && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Endpoint:</span>
                          <span className="font-mono text-xs text-text-primary truncate max-w-[60%]">
                            {copilotConfig.endpoint || '(blank)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">API Key:</span>
                          <span className="font-mono text-xs text-text-primary">
                            {copilotConfig.apiKey
                              ? maskKey(copilotConfig.apiKey)
                              : '(blank)'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Model:</span>
                          <span className="font-mono text-xs text-text-primary">
                            {copilotConfig.model || '(default)'}
                          </span>
                        </div>
                      </>
                    )}
                    {mcpConfig && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Command:</span>
                          <span className="font-mono text-xs text-text-primary">
                            {mcpConfig.command || '(blank)'}
                          </span>
                        </div>
                        {mcpConfig.args && mcpConfig.args.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-text-secondary">Args:</span>
                            <span className="font-mono text-xs text-text-primary truncate max-w-[60%]">
                              {mcpConfig.args.join(' ')}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border-default px-6 py-4">
          <Button
            variant="secondary"
            onClick={step === 'framework' ? handleClose : handleBack}
            disabled={isSubmitting}
          >
            {step === 'framework' ? (
              'Cancel'
            ) : (
              <>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </>
            )}
          </Button>

          {step === 'review' ? (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              isLoading={isSubmitting}
            >
              Install Agent
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={!canProceed()}>
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
